import { useGSAP } from '@gsap/react';
import clsx from 'clsx';
import Link from 'next/link';
import { FC, ReactNode, useEffect, useRef, useState } from 'react';

import { gsap } from '@/lib/gsap';

import css from './style.module.scss';

export interface AnimatedButtonProps {
	type?: 'button' | 'link';
	href?: string;
	target?: '_blank' | '_self' | '_parent' | '_top';
	wrapperClassName?: string;
	className?: string;
	classNameText?: string;
	startIconComponent?: ReactNode | ReactNode[];
	endIconComponent?: ReactNode | ReactNode[];
	children: string;
	onClick?: () => void;
	disabled?: boolean;
	buttonType?: 'button' | 'submit' | 'reset';
}

const AnimatedButton: FC<AnimatedButtonProps> = ({
	href = '/',
	type = 'button',
	target = '_self',
	className,
	classNameText = '',
	startIconComponent = null,
	endIconComponent = null,
	children,
	onClick,
	wrapperClassName,
	disabled = false,
	buttonType = 'button'
}) => {
	const topRef = useRef<HTMLSpanElement>(null);
	const botRef = useRef<HTMLSpanElement>(null);
	const wrapperRef = useRef<HTMLDivElement>(null);
	const [text, setText] = useState('');

	const innerRef = useRef<HTMLButtonElement & HTMLAnchorElement>(null);
	const tlRef = useRef<gsap.core.Timeline | null>(null);
	const disabledRef = useRef(disabled);

	useEffect(() => {
		disabledRef.current = disabled;
	}, [disabled]);

	useEffect(() => {
		if (typeof children === 'string') setText(children);
	}, [children]);

	useGSAP(
		() => {
			let mouseMoveHandler: ((e: MouseEvent) => void) | null = null;
			const wrapper = wrapperRef.current;

			const matchMedia = gsap.matchMedia();

			matchMedia.add(
				{
					isDesktop: '(min-width: 992px)',
					isMobile: '(max-width: 991px)'
				},
				context => {
					const { isMobile } = context.conditions as gsap.Conditions;
					if (isMobile) return;

					const top = topRef.current;
					const bot = botRef.current;

					if (!top || !bot || !wrapper) return;

					const tl = gsap.timeline({ paused: true });

					tl.to(wrapper, { x: -5, y: 5, duration: 0.3, ease: 'power2.out' }, 0)
						.fromTo(
							top,
							{ yPercent: 0, opacity: 1 },
							{ yPercent: -100, opacity: 0, duration: 0.1, ease: 'power2.out' },
							0
						)
						.fromTo(
							bot,
							{ yPercent: 100, opacity: 0 },
							{ yPercent: 0, opacity: 1, duration: 0.1, ease: 'power2.out' },
							0.05
						);

					tlRef.current = tl;

					mouseMoveHandler = (e: MouseEvent) => {
						if (disabledRef.current) return;

						const currentX = (gsap.getProperty(wrapper, 'x') as number) || 0;
						const currentY = (gsap.getProperty(wrapper, 'y') as number) || 0;

						const rect = wrapper.getBoundingClientRect();
						const stableLeft = rect.left - currentX;
						const stableTop = rect.top - currentY;

						const x = e.clientX - (stableLeft + rect.width / 2);
						const y = e.clientY - (stableTop + rect.height / 2);

						const maxOffset = 10;
						const clampedX = Math.max(-maxOffset, Math.min(maxOffset, x));
						const clampedY = Math.max(-maxOffset, Math.min(maxOffset, y));

						gsap.to(wrapper, {
							x: clampedX,
							y: clampedY,
							ease: 'power3.out',
							duration: 0.6,
							overwrite: 'auto'
						});
					};

					wrapper.addEventListener('mousemove', mouseMoveHandler);
					wrapper.addEventListener('mouseleave', () => {
						if (disabledRef.current) return;

						gsap.to(wrapper, {
							x: 0,
							y: 0,
							ease: 'power3.out',
							duration: 0.6,
							overwrite: 'auto'
						});
					});

					return () => {
						if (wrapper && mouseMoveHandler)
							wrapper.removeEventListener('mousemove', mouseMoveHandler);
					};
				}
			);
		},
		{ scope: wrapperRef, dependencies: [disabled] }
	);

	const onEnter = () => {
		if (!disabled) tlRef.current?.play();
	};
	const onLeave = () => {
		if (!disabled) tlRef.current?.reverse();
	};

	const content = (
		<div className={clsx(css.glitchText, classNameText)} data-text={text}>
			<span ref={topRef} className={css.top}>
				{children}
			</span>
			<span ref={botRef} className={css.bot}>
				{children}
			</span>
		</div>
	);

	return (
		<div
			ref={wrapperRef}
			className={clsx(css.wrapper, wrapperClassName, disabled && css.disabled)}
			onMouseEnter={onEnter}
			onMouseLeave={onLeave}
		>
			{type === 'link' ? (
				<Link
					ref={innerRef as any}
					href={href}
					target={target}
					className={clsx(className)}
					onClick={onClick}
				>
					{startIconComponent ? (
						<span className="pointer-events-none">{startIconComponent}</span>
					) : null}
					{content}
					{endIconComponent ? (
						<span className="pointer-events-none">{endIconComponent}</span>
					) : null}
				</Link>
			) : (
				<button
					ref={innerRef as any}
					type={buttonType}
					className={clsx(className)}
					disabled={disabled}
					onClick={onClick}
				>
					{startIconComponent ? (
						<span className="pointer-events-none">{startIconComponent}</span>
					) : null}
					{content}
					{endIconComponent ? (
						<span className="pointer-events-none">{endIconComponent}</span>
					) : null}
				</button>
			)}
		</div>
	);
};

export default AnimatedButton;
